/**
 * Fallback subscription tiers, billing intervals, and HKD prices.
 * Live amounts and promotions are stored in admin.db and edited at /admin/pricing.
 * These constants apply until that product is saved.
 * Display copy lives in messages/{locale}/Plans — keep only ids, amounts, and feature keys here.
 * Airwallex amounts use major currency units (99 = HK$99).
 * Existing Airwallex subscriptions stay on the price they bought; a later
 * public-price change creates a new Price for new checkouts only.
 */

export type PlanTier = "starter" | "professional" | "enterprise";
export type BillingInterval = "monthly" | "yearly";

export type CheckoutPlanId =
  | "starter-monthly"
  | "starter-yearly"
  | "professional-monthly"
  | "professional-yearly";

export const STARTER_FEATURE_KEYS = [
  "nlToCode",
  "dataSources",
  "backtesting",
  "emailSupport",
] as const;

export const PROFESSIONAL_FEATURE_KEYS = [
  "everythingBasic",
  "liveGateways",
  "bloombergConnectors",
  "riskMonitoring",
  "prioritySupport",
] as const;

/** Card checkout for Basic starts with this many days before the first charge. */
export const BASIC_TRIAL_DAYS = 14;

export const ENTERPRISE_FEATURE_KEYS = [
  "everythingPro",
  "customConnectors",
  "liveIntegrations",
  "onboarding",
  "sla",
] as const;

export interface PlanTierDefinition {
  id: PlanTier;
  featureKeys: readonly string[];
  highlighted?: boolean;
  monthlyAmountHkd: number | null;
  yearlyAmountHkd: number | null;
}

export const PLAN_TIERS: PlanTierDefinition[] = [
  {
    id: "starter",
    monthlyAmountHkd: 99,
    yearlyAmountHkd: 990,
    featureKeys: STARTER_FEATURE_KEYS,
  },
  {
    id: "professional",
    monthlyAmountHkd: 299,
    yearlyAmountHkd: 2990,
    highlighted: true,
    featureKeys: PROFESSIONAL_FEATURE_KEYS,
  },
  {
    id: "enterprise",
    monthlyAmountHkd: null,
    yearlyAmountHkd: null,
    featureKeys: ENTERPRISE_FEATURE_KEYS,
  },
];

export const CURRENCY = "HKD" as const;

export function formatHkd(amount: number, locale = "en-HK"): string {
  return `HK$${amount.toLocaleString(locale)}`;
}

export function checkoutPlanId(
  tier: PlanTier,
  interval: BillingInterval
): CheckoutPlanId | null {
  if (tier === "enterprise") return null;
  return `${tier}-${interval}` as CheckoutPlanId;
}

export function parseCheckoutPlanId(planId: string): {
  tier: PlanTier;
  interval: BillingInterval;
} | null {
  const match = /^(starter|professional)-(monthly|yearly)$/.exec(planId);
  if (!match) return null;
  return {
    tier: match[1] as PlanTier,
    interval: match[2] as BillingInterval,
  };
}

/** Back-compat: bare tier ids from older URLs default to yearly checkout. */
export function normalizeCheckoutPlanId(planId: string): string {
  if (planId === "starter" || planId === "professional") {
    return `${planId}-yearly`;
  }
  return planId;
}

export function getTierDefinition(tier: PlanTier): PlanTierDefinition | undefined {
  return PLAN_TIERS.find((p) => p.id === tier);
}

export function getCheckoutAmountHkd(planId: string): number | null {
  const parsed = parseCheckoutPlanId(normalizeCheckoutPlanId(planId));
  if (!parsed) return null;
  const tier = getTierDefinition(parsed.tier);
  if (!tier) return null;
  return parsed.interval === "monthly"
    ? tier.monthlyAmountHkd
    : tier.yearlyAmountHkd;
}

/** English fallback for emails / server logs when no translator is available. */
const EN_TIER_NAMES: Record<PlanTier, string> = {
  starter: "Basic",
  professional: "Pro Research",
  enterprise: "Institutional",
};

export function getPlanDisplayName(planId: string): string {
  const parsed = parseCheckoutPlanId(normalizeCheckoutPlanId(planId));
  if (!parsed) return planId;
  const name = EN_TIER_NAMES[parsed.tier];
  const intervalLabel = parsed.interval === "monthly" ? "Monthly" : "Yearly";
  return `${name} (${intervalLabel})`;
}

export type PlanPriceLabels = {
  custom: string;
  periodMo: string;
  periodYr: string;
  annualDiscount: string;
  yearlySublabel: (monthly: string, discount: string) => string;
};

export function getTierPriceLabel(
  tier: PlanTierDefinition,
  interval: BillingInterval,
  labels: PlanPriceLabels,
  locale = "en-HK"
): { price: string; period: string; sublabel?: string } {
  if (tier.id === "enterprise") {
    return { price: labels.custom, period: "" };
  }
  const amount =
    interval === "monthly" ? tier.monthlyAmountHkd! : tier.yearlyAmountHkd!;
  if (interval === "monthly") {
    return { price: formatHkd(amount, locale), period: labels.periodMo };
  }
  const monthlyEquivalent = Math.round(amount / 12);
  const monthlyFmt = formatHkd(monthlyEquivalent, locale);
  return {
    price: formatHkd(amount, locale),
    period: labels.periodYr,
    sublabel: labels.yearlySublabel(monthlyFmt, labels.annualDiscount),
  };
}
